import {
  CheckmarkIcon,
  CrossIcon,
  CurecallCopyButton,
  CurecallModalContainer,
  ECurecallSize,
  LoadingSpinner,
  RoundInfoIcon,
} from '@honestica/curecall-ui'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { LifenService } from 'src/services/lifen.service'

type TLifenPatientSearchModalProps = {
  projectId: number
  remoteId: string
  isOpen: boolean
  onToggle: VoidFunction
  hasPersistedLifenPatientRef: boolean
}

const StepBlock = ({
  title,
  isSuccess,
  children,
}: {
  title: string
  isSuccess: boolean
  children: React.ReactNode
}) => (
  <div className="c-flex c-flex-col c-gap-1 c-rounded c-border c-border-grey-200 c-p-3">
    <div className="c-flex c-items-center c-gap-2 c-leading-none">
      {isSuccess ? (
        <CheckmarkIcon className="c-h-4 c-w-4 c-shrink-0 c-text-green-500" />
      ) : (
        <CrossIcon className="c-h-4 c-w-4 c-shrink-0 c-text-red-500" />
      )}
      <span className="c-text-xs c-font-semibold c-uppercase c-tracking-wide c-text-grey-700 c-leading-none">
        {title}
      </span>
    </div>
    {children}
  </div>
)

export const LifenPatientSearchModal = ({
  projectId,
  remoteId,
  isOpen,
  onToggle,
  hasPersistedLifenPatientRef,
}: TLifenPatientSearchModalProps) => {
  const { t } = useTranslation()

  const {
    isLoading,
    error,
    data: diagnostic,
  } = useQuery({
    queryKey: ['lifen-patient-search', projectId, remoteId],
    queryFn: () => LifenService.searchPatient(projectId, remoteId),
    enabled: isOpen,
  })

  const entries = diagnostic?.patientSearch?.response?.entry ?? []

  return (
    <CurecallModalContainer
      title={t('pages.patient-page.lifen-patient-search.modal-title')}
      isOpen={isOpen}
      toggle={onToggle}
      closeModalAriaLabel={t('pages.patient-page.lifen-patient-search.modal-close-aria-label')}
      dialogClassName="c-w-[500px]"
    >
      {isLoading && (
        <div className="c-flex c-flex-1 c-items-center c-justify-center">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && (error || !diagnostic) && (
        <p>{t('common.error.generic')}</p>
      )}

      {!isLoading && diagnostic && (
        <div className="c-flex c-flex-col c-gap-4">
          <StepBlock
            title={t('pages.patient-page.lifen-patient-search.token-acquisition-title')}
            isSuccess={diagnostic.tokenAcquisition.success}
          >
            {diagnostic.tokenAcquisition.success ? (
              <p>
                {t('pages.patient-page.lifen-patient-search.token-acquisition-success')}
                {diagnostic.tokenAcquisition.scopes &&
                  ` — ${t('pages.patient-page.lifen-patient-search.token-acquisition-scopes', {
                    scopes: diagnostic.tokenAcquisition.scopes,
                  })}`}
              </p>
            ) : (
              <p className="c-text-orange-700">
                {t('pages.patient-page.lifen-patient-search.token-acquisition-error', {
                  error: diagnostic.tokenAcquisition.error,
                })}
              </p>
            )}
          </StepBlock>

          <StepBlock
            title={t('pages.patient-page.lifen-patient-search.patient-search-title')}
            isSuccess={!!diagnostic.patientSearch?.success}
          >
            {!diagnostic.patientSearch && (
              <p>{t('pages.patient-page.lifen-patient-search.patient-search-not-run')}</p>
            )}
            {diagnostic.patientSearch && !diagnostic.patientSearch.success && (
              <p className="c-text-orange-700">
                {t('pages.patient-page.lifen-patient-search.patient-search-error', {
                  error: diagnostic.patientSearch.error,
                })}
              </p>
            )}
            {diagnostic.patientSearch?.success && entries.length === 0 && (
              <p>{t('pages.patient-page.lifen-patient-search.patient-search-no-match')}</p>
            )}
            {diagnostic.patientSearch?.success && entries.length > 0 && (
              <div className="c-flex c-flex-col c-gap-1">
                <p>
                  {entries.length === 1
                    ? t('pages.patient-page.lifen-patient-search.patient-search-matched')
                    : t('pages.patient-page.lifen-patient-search.patient-search-ambiguous', {
                        count: entries.length,
                      })}
                </p>
                {entries.map((entry) => (
                  <div key={entry.resource.id} className="c-flex c-items-center c-gap-2">
                    <CurecallCopyButton
                      ariaLabelText={t('pages.patient-page.lifen-patient-search.copy-value-aria-label', {
                        label: t('pages.patient-page.lifen-patient-search.lifen-resource-id'),
                      })}
                      data={entry.resource.id}
                      size={ECurecallSize.SMALL}
                    />
                    <span className="c-truncate">{entry.resource.id}</span>
                  </div>
                ))}
              </div>
            )}
          </StepBlock>

          {diagnostic.patientSearch?.success && entries.length === 1 && !hasPersistedLifenPatientRef && (
            <div className="c-flex c-items-start c-gap-2 c-rounded c-border c-border-blue-200 c-bg-blue-50 c-p-3">
              <RoundInfoIcon className="c-h-4 c-w-4 c-shrink-0 c-mt-0.5 c-text-blue-500" />
              <p className="c-text-sm c-text-blue-700">
                {t('pages.patient-page.lifen-patient-search.not-persisted-info')}
              </p>
            </div>
          )}
        </div>
      )}
    </CurecallModalContainer>
  )
}
